/**
 * `02/ Inquiries/ Record` (12:7467).
 *
 * Built from its nodes: `Record/Header` (7:54) — a `Caption/12` breadcrumb over an `H1/24`
 * customer name, a `StagePill`, the "‹ 3 of 12 ›" pager and the action cluster; `Record/Tabs`
 * (12:7810); a body of tab content beside `Record/PropertiesPanel` at 320 wide.
 *
 * ===========================================================================
 * THE DESIGN AUTHORED ONE TAB BODY, AND IT IS THE ONE THIS PHASE CANNOT BUILD
 * ===========================================================================
 * The only `TabContent` in the frame is `Record/LineItemsTable` (12:7841) — the QUOTATION
 * tab, with line items, subtotal/discount/total and a "Line total vs Quoted" reconciliation
 * strip. Quotes are Phase 3; there is no quotes read in this API surface. So:
 *
 *  · **Quotation** and **Files** tabs: omitted (as on the Peek — same reasons).
 *  · **Overview**: authored, because Figma never drew one. It is assembled from components
 *    the design DID author — the properties panel groups, the activity row, the stage pill,
 *    the days pill — and it shows the fields `GET /inquiries/:id` actually returns. This is a
 *    recorded extension, not a reconstruction of something that exists.
 *  · **Activity**: the country event feed filtered to this inquiry. Also where an Undo is
 *    visible as its own row, which is the point of Part A's undo semantics.
 *
 * `Record/StageStepper` (12:7787) — Inquiry → CRF → Development → Deployment → Sign Off — is
 * NOT rendered. Four of its five steps are Phase 3/4 entities that do not exist yet
 * (`crfs` has rows but no module in this phase; work orders, deployment and sign-off are
 * later). A five-step stepper with four fabricated states would assert progress this system
 * cannot know.
 *
 * "Request Quote" (7:69) is omitted for the same reason: it is the Phase 3 action.
 *
 * ===========================================================================
 * THE "3 OF 12" PAGER IS REAL, AND BOUNDED BY THE PAGE IT CAME FROM
 * ===========================================================================
 * The pager reads the SAME list query the operator arrived from — the URL carries the filters
 * (`query-state.ts`), so the record page can compute the row's absolute position from
 * `(page-1)*perPage + index + 1` against the list's own `total`. Prev/next step within the
 * loaded page and are DISABLED at its edges, with a title saying so, because stepping past a
 * page boundary would need a row this client does not have. Guessing a neighbour — or hiding
 * the pager because it is not perfect — are both worse than a control that is honest about
 * its range.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { Alert, CountryBadge, DaysPill, SkeletonBlock, StagePill } from '../../components/ui';
import { UserAvatar } from '../avatars/UserAvatar';
import { BOARD_STAGES, daysInStage, stageTabLabel } from '../../domain/stages';
import { cn } from '../../lib/cn';
import { RAIL_GEOMETRY, stageRailClass } from './stage-rail';
import { MenuItem } from './Toolbar';
/* WT5 — `./panel` and `./PeekPanel` died with the slide-over kill; the tab row
 * and property rows relocated verbatim, the error sentences to `./errors`. */
import { PanelTabs, PropertyGroup, PropertyRow } from './record-furniture';
import { RequirementsGroup } from './RequirementsGroup';
import { formatDateLong, tallyEditionLabel } from './format';
import { FollowupTimeline, InquiryActivityFeed, formatWhen } from './Timeline';
import { LostReasonDialog } from './LostReasonDialog';
import { peekErrorText } from './errors';
import { useInquiryQuery } from './query-state';
import {
  useInquiryEvents,
  useInquiryList,
  useInquiryRecord,
  useParty,
  useStageMoveController,
} from './use-inquiries';
import type { MoveSource } from './stage-move';

type RecordTab = 'overview' | 'activity';

export function InquiryRecordPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { inquiryId } = useParams<{ inquiryId: string }>();
  const { can } = useSession();
  const { state, search } = useInquiryQuery();
  const navigate = useNavigate();

  const [tab, setTab] = useState<RecordTab>('overview');

  const canView = can(countryCode, 'inquiries.view');
  const canEdit = can(countryCode, 'inquiries.edit');
  const canStage = can(countryCode, 'inquiries.stage');

  const id = inquiryId ?? null;
  const record = useInquiryRecord(canView ? id : null);
  const events = useInquiryEvents(countryCode, canView ? id : null, tab === 'activity');
  const party = useParty(record.data?.party_id ?? null, can(countryCode, 'parties.view'));
  const mover = useStageMoveController(countryCode);

  // The list the operator came from — same query key, so this is a cache hit when they
  // navigated from the table and one request when the URL was pasted cold.
  const list = useInquiryList(countryCode, state, canView);

  if (!canView) {
    return (
      <div className="p-6">
        <Alert tone="danger" title="This record is not available to this account">
          Viewing inquiries in {countryCode} needs the Inquiries permission.
        </Alert>
      </div>
    );
  }

  if (record.isPending) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <SkeletonBlock className="h-4 w-64" />
        <SkeletonBlock className="h-8 w-80" />
        <SkeletonBlock className="h-64 w-full" />
      </div>
    );
  }

  if (record.isError || record.data === undefined) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="This inquiry could not be opened">
          {peekErrorText(record.error)}
        </Alert>
        <Link
          to={`/countries/${countryCode}/inquiries${search}`}
          className="w-fit text-body13 text-indigo hover:underline"
        >
          Back to the pipeline
        </Link>
      </div>
    );
  }

  const inquiry = record.data;
  const days = daysInStage(inquiry.stage_entered_at);
  const source: MoveSource = {
    id: inquiry.id,
    doc_no: inquiry.doc_no,
    stage: inquiry.stage,
    lost_reason: inquiry.lost_reason,
  };

  const rows = list.data?.data ?? [];
  const indexOnPage = rows.findIndex((row) => row.id === inquiry.id);
  const absolute =
    indexOnPage === -1 ? null : (state.page - 1) * state.perPage + indexOnPage + 1;
  const prev = indexOnPage > 0 ? rows[indexOnPage - 1] : undefined;
  const next = indexOnPage === -1 ? undefined : rows[indexOnPage + 1];

  const go = (targetId: string): void => {
    navigate(`/countries/${countryCode}/inquiries/${targetId}${search}`);
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      {/*
       * L3 — THE SPINE, RECORD SIDE. The same 3px stage stripe the peek panel draws,
       * here running the height of `Record/Header`: breadcrumb, title, pill, pager and
       * actions all sit to the right of it, so the record announces which column it
       * belongs to before a word of it is read.
       *
       * THE RAIL IS ABSOLUTE, BLEEDING LEFT — like the peek's and the table's, and
       * unlike this header's first draft, which put it IN FLOW behind a 12px gap.
       * In-flow, the rail + gutter pushed every header line 15px right of the content
       * card below it, breaking the page's shared left edge — an adversarial diff of
       * the pass's own before/after screenshots caught the drift, and the honesty
       * ledger's "nothing else changed position" claim was false until this matched
       * its two siblings. `-left-[15px]` parks the 3px rail where the gutter was, so
       * the header text sits exactly where it always sat and the rail hangs in the
       * page's existing left margin. All three rails now displace nothing.
       */}
      <header className="relative flex items-stretch">
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-y-0 -left-[15px]',
            RAIL_GEOMETRY,
            stageRailClass(inquiry.stage),
          )}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {/* `Record/Header` breadcrumb (7:55). */}
          <p className="text-label text-fg-muted">
            <Link to={`/countries/${countryCode}`} className="hover:underline">
              Pipeline
            </Link>
            {' / '}
            <Link to={`/countries/${countryCode}/inquiries${search}`} className="hover:underline">
              Inquiries
            </Link>
            {' / '}
            <span className="font-mono text-mono">{inquiry.doc_no}</span>
          </p>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              {/* PHASE 3a — the record header's country, ahead of the customer name.
               * `countryCode` is `useCountryCode()`, i.e. the same string the record
               * was fetched with; see `CountryBadge` for why that is the honest
               * source and not a guess. */}
              <CountryBadge code={countryCode} />
              <h1 className="truncate text-h1 text-fg">
                {inquiry.party_name ?? 'Unknown customer'}
              </h1>
              <StagePill stage={inquiry.stage} />
              <DaysPill days={days} long />

              {absolute === null ? null : (
                <span className="flex items-center gap-1.5">
                  <PagerButton
                    label="Previous inquiry"
                    disabled={prev === undefined}
                    title={
                      prev === undefined
                        ? 'This is the first row on the page you came from'
                        : undefined
                    }
                    onClick={() => {
                      if (prev !== undefined) go(prev.id);
                    }}
                  >
                    <ChevronLeft aria-hidden="true" className="size-4" />
                  </PagerButton>
                  <span className="tnum text-label text-fg-muted">
                    {absolute} of {list.data?.total ?? absolute}
                  </span>
                  <PagerButton
                    label="Next inquiry"
                    disabled={next === undefined}
                    title={
                      next === undefined
                        ? 'This is the last row on the page you came from'
                        : undefined
                    }
                    onClick={() => {
                      if (next !== undefined) go(next.id);
                    }}
                  >
                    <ChevronRight aria-hidden="true" className="size-4" />
                  </PagerButton>
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {canEdit ? (
                /* WT5 — the follow-up is a ROUTE now (`…/follow-up`, keeping
                 * the list context in `?…`), so the action is a real anchor.
                 * Classes are `Button`'s ghost variant verbatim. */
                <Link
                  to={`/countries/${countryCode}/inquiries/${inquiry.id}/follow-up${search}`}
                  className="inline-flex h-9 select-none items-center justify-center gap-2 rounded-sm border border-line px-3 text-body font-medium text-fg-secondary transition-colors hover:bg-subtle hover:text-fg"
                >
                  Add Follow-up
                </Link>
              ) : null}
              {canStage ? (
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger
                    aria-label="More actions"
                    className="flex size-9 items-center justify-center rounded-sm border border-line bg-surface text-fg-secondary hover:text-fg"
                  >
                    <MoreHorizontal aria-hidden="true" className="size-4" />
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Portal>
                    <DropdownMenu.Content
                      align="end"
                      sideOffset={4}
                      className="z-50 w-[240px] rounded-md border border-line bg-surface p-1.5 shadow-[var(--crf-shadow-overlay)]"
                    >
                      <DropdownMenu.Label className="px-2 py-1 text-th uppercase text-fg-muted">
                        Move to stage
                      </DropdownMenu.Label>
                      {BOARD_STAGES.filter((stage) => stage !== inquiry.stage).map((stage) => (
                        <MenuItem key={stage} onSelect={() => mover.requestMove(source, stage)}>
                          {stageTabLabel(stage)}
                        </MenuItem>
                      ))}
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      <PanelTabs<RecordTab>
        active={tab}
        onSelect={setTab}
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'activity', label: 'Activity', count: events.data?.total ?? null },
        ]}
      />

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-5">
          {tab === 'overview' ? (
            <>
              <section className="flex flex-col gap-3 rounded-md border border-line bg-surface p-5">
                <h2 className="text-h3 text-fg">
                  {inquiry.subject === null || inquiry.subject === ''
                    ? 'No subject recorded'
                    : inquiry.subject}
                </h2>
                <p className="whitespace-pre-wrap text-body text-fg-secondary">
                  {inquiry.details === null || inquiry.details === ''
                    ? 'No details were captured with this inquiry.'
                    : inquiry.details}
                </p>
                {inquiry.lost_reason === null ? null : (
                  <Alert tone="danger" title={`Marked ${stageTabLabel('lost')}`}>
                    {inquiry.lost_reason}
                  </Alert>
                )}
              </section>

              <section className="flex flex-col gap-3">
                <h2 className="text-th uppercase text-fg-muted">Follow-ups</h2>
                <FollowupTimeline
                  followups={inquiry.followups}
                  emptyMessage="No follow-ups yet. Every call logged here shows on the timeline."
                />
              </section>
            </>
          ) : (
            <section className="flex flex-col gap-3">
              {events.isPending ? <SkeletonBlock className="h-32 w-full" /> : null}
              {events.isError ? (
                <Alert tone="warning">The activity trail could not be loaded.</Alert>
              ) : null}
              {events.data === undefined ? null : (
                <InquiryActivityFeed events={events.data.data} />
              )}
            </section>
          )}
        </div>

        {/* `Record/PropertiesPanel` at 320 (12:7875). */}
        <aside className="flex w-full shrink-0 flex-col gap-5 rounded-md border border-line bg-surface p-5 lg:w-[320px]">
          <PropertyGroup label="Customer">
            <PropertyRow name="Customer">{inquiry.party_name ?? '—'}</PropertyRow>
            {can(countryCode, 'parties.view') ? (
              <>
                <PropertyRow name="Phone">{party.data?.phone ?? '—'}</PropertyRow>
                <PropertyRow name="Email">{party.data?.email ?? '—'}</PropertyRow>
                <PropertyRow name="City">{party.data?.city ?? '—'}</PropertyRow>
                <PropertyRow name="Tax no">{party.data?.tax_no ?? '—'}</PropertyRow>
              </>
            ) : (
              <p className="text-label text-fg-muted">
                Contact details need the Parties permission.
              </p>
            )}
          </PropertyGroup>

          {/*
            ⚠ THE `Value` PROPERTY GROUP STOOD HERE AND IS GONE WITH ITS ONE ROW.
            `Expected` rendered `formatMoney(inquiry.expected_value, inquiry.currency)`,
            masked-aware, and migration 44 drops both columns (owner ruling Q6). The GROUP
            goes with the row rather than being left with an em dash in it: a
            `PropertyGroup` whose only child is a permanent `—` is a heading that promises
            a fact this record does not have, which is the shape `VISUAL-HONESTY.md` files
            under "not built | BY SCHEMA" rather than drawing.

            The masked marker it carried at `text-fg-secondary` — MEASURED 4.64 light /
            5.85 dark, replacing a `text-fg-muted` that was 2.63:1 on this panel's
            `bg/surface` against a 4.5 floor — is retired from
            `renewals/masked-marker.contrast.test.tsx` in this same commit. `CrfRecordPage`
            ships the identical `cn('tnum', masked && …)` idiom on `crfs.amount` and is
            still measured there, so the record-panel case is not left unproven.
          */}

          {/*
            THE TALLY DETAILS — migration 45, owner ruling Q9 (`A only`).

            ⚠ THE GROUP RENDERS EVEN WHEN ALL THREE ARE NULL, AND THAT IS THE DECISION.
            Every other conditional block on this panel is hidden for a REASON THE READER
            CANNOT SEE — `Legacy ref` is hidden when absent, the customer contact rows are
            replaced when `parties.view` is missing, and the `VALUE` group above was
            deleted outright when its columns went. If this one vanished too, an operator
            would have no way to tell "nobody has asked yet" from "you may not see this",
            and those are opposite facts. These fields are OPTIONAL, not PERMISSIONED: `—`
            is this record's spelling of "nobody knows", and it is the honest answer here.

            ⚠ AND THE HEADING ALONE IS NOT ENOUGH, WHICH IS WHY THE SENTENCE IS INSIDE THE
            GROUP RATHER THAN IN A TOOLTIP. Owner ruling Q9: *"It is a claim on a phone
            call and should read as one."* A `Serial` row rendered like `Number` above it
            would read as something the system knows. Nothing verifies these:
            `tally_licence_customers.party_id` is NULL for every row, so there is no path
            from this customer to a register entry to check against (decision D-2), and
            the register has no edition or release column to check the other two against
            under any shape.

            The serial is `mono` because `Row/Tally Serial` (I12:6449;8:56) is the design's
            one mono value and it is the reason `PropertyRow` has the prop at all — an
            identifier read out character by character over the phone.
          */}
          <PropertyGroup label="Tally details">
            <p className="text-label text-fg-secondary">
              What the customer told us on the call. Not checked against the licence
              register.
            </p>
            <PropertyRow name="Serial" mono>
              {inquiry.tally_serial ?? '—'}
            </PropertyRow>
            <PropertyRow name="Edition">{tallyEditionLabel(inquiry.tally_edition)}</PropertyRow>
            <PropertyRow name="Release">{inquiry.tally_release ?? '—'}</PropertyRow>
          </PropertyGroup>

          {/*
            THE REQUIREMENTS — the shared platform vocabulary as this inquiry carries it
            (migration 48, owner ruling 5; IQ-9 / D19), rendered and — since unit 3.2 —
            EDITED by `RequirementsGroup`. It moved to its own file with the editor: the
            read view is unchanged in there (same testid, same rows, same empty
            sentence), and the edit affordance is gated on the SAME `inquiries.edit`
            this header's Add Follow-up uses, because that is what `PATCH
            /inquiries/:id` itself demands. The whole argument — including why an
            untouched set sends nothing and why `[]` must reach the wire — lives in
            that file's header.
          */}
          <RequirementsGroup countryCode={countryCode} inquiry={inquiry} canEdit={canEdit} />

          <PropertyGroup label="Assignment">
            {/* L4. 24px, the same as the peek and the table row — one datum, one size.
             * 32 and 40 are deliberately NOT used on this page: the only thing here big
             * enough to carry them is the `H1/24`, and that names a PARTY (a company),
             * so putting a person's disc beside it would label the record with the wrong
             * subject. The record's person is its owner, and its owner lives here. */}
            <PropertyRow name="Owner">
              {inquiry.assigned_to_name === null ? (
                'Unassigned'
              ) : (
                <span className="inline-flex min-w-0 max-w-full items-center gap-2 align-middle">
                  {/* 2.5b: photo when present — same disc as the table and peek. */}
                  <UserAvatar
                    name={inquiry.assigned_to_name}
                    userId={inquiry.assigned_to}
                    size={24}
                    decorative
                  />
                  <span className="truncate">{inquiry.assigned_to_name}</span>
                </span>
              )}
            </PropertyRow>
          </PropertyGroup>

          <PropertyGroup label="System">
            <PropertyRow name="Number" mono>
              {inquiry.doc_no}
            </PropertyRow>
            {inquiry.legacy_ref === null ? null : (
              <PropertyRow name="Legacy ref" mono>
                {inquiry.legacy_ref}
              </PropertyRow>
            )}
            <PropertyRow name="Created">{formatDateLong(inquiry.created_at)}</PropertyRow>
            <PropertyRow name="In stage since">
              {formatWhen(inquiry.stage_entered_at)}
            </PropertyRow>
            <PropertyRow name="Updated">{formatWhen(inquiry.updated_at)}</PropertyRow>
          </PropertyGroup>
        </aside>
      </div>

      <LostReasonDialog
        move={mover.awaitingReason}
        busy={mover.isMoving}
        onSubmit={mover.submitReason}
        onCancel={mover.cancelReason}
      />
    </div>
  );
}

function PagerButton({
  label,
  disabled,
  title,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  title?: string;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={title}
      disabled={disabled}
      onClick={onClick}
      className="flex size-7 items-center justify-center rounded-sm border border-line bg-surface text-fg-secondary hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
